import { handleAxiosError } from '@/api/helpers';
import { Account } from '@/client/accounts';
import { EntityEnum } from '@/client/users';
import { AppRoutesPaths, LoadingStatuses } from '@/common/constants';
import { useUsersImportMessage } from '@/components/users';
import { useAccountUsersImport } from '@/hooks/query/accounts.hooks';
import { useBranchUsersImport } from '@/hooks/query/branches.hooks';
import { useGroupUsersImport } from '@/hooks/query/user-groups.hooks';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AppSpinner } from '@/ui/app-spinner';
import { AppButton } from '@/ui/buttons';
import { AppCheckbox } from '@/ui/checkbox/AppCheckbox';
import { FlexContainer } from '@/ui/styled-ui';
import {
  getIsActivePhishingStatus,
  InfoCircleIcon,
  randomString,
  WarningTriangleIcon,
} from '@/utils/helpers';
import { PostHogFeatureFlags } from '@/utils/posthog';
import {
  FREE_TRIAL_USER_LIMIT,
  isTrialRestricted,
} from '@/utils/posthog/posthog.helper';
import { AxiosError } from 'axios';
import { CheckboxChangeEvent } from 'primereact/checkbox';
import { Dialog, DialogProps } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { InputSwitch } from 'primereact/inputswitch';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  width: 500px;
`;

const StyledDescription = styled.p`
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  font-weight: 500;
  margin-bottom: var(--medium-padding);
`;

const StyledImpactMessage = styled(FlexContainer)`
  background-color: var(--beige-main);
  border-radius: var(--medium-border-radius);
  padding: var(--semi-big-padding);
  margin-top: var(--medium-padding);
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  min-height: 196px;

  & p {
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    font-weight: 700;
    text-align: center;
  }

  .text {
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
    font-weight: 500;
    margin: 0;
    width: max-content;
  }

  .number {
    font-size: var(--heading-medium-font-size);
    line-height: var(--heading-medium-line-height);
    font-weight: 700;
  }

  .no-changes {
    min-height: 40px;
    color: var(--grey-darker);
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    font-weight: 400;
    margin: 0;
    width: max-content;
  }
`;

const StyledUnverifiedDomainWarning = styled.div`
  background-color: var(--yellow-pale);
  border-radius: var(--small-border-radius);
  padding: var(--default-padding-small) var(--default-padding);
  margin-top: var(--medium-padding);
  padding-left: var(--default-padding-small);
  display: flex;
  gap: var(--xsmall-padding);
  align-items: flex-start;

  svg {
    flex-shrink: 0;
  }

  .warning-text {
    display: flex;
    flex-direction: column;
    gap: var(--small-padding);

    p {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      font-weight: 400;
      margin: 0;
      text-align: left;
    }
  }
`;

const StyledSoftDeletedWarning = styled.div`
  background-color: var(--blue-pale);
  border-radius: var(--small-border-radius);
  padding: var(--default-padding-small) var(--default-padding);
  margin-top: var(--medium-padding);
  padding-left: var(--default-padding-small);
  display: flex;
  gap: var(--xsmall-padding);
  align-items: flex-start;
  align-self: stretch;

  svg {
    flex-shrink: 0;
    min-width: 20px;
    path {
      fill: var(--blue-dark);
    }
  }

  .warning-text {
    display: flex;
    flex-direction: column;
    gap: var(--small-padding);

    p {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      font-weight: 400;
      margin: 0;
      text-align: left;
    }
  }

  .restore-toggle {
    display: flex;
    align-items: center;
    gap: var(--xsmall-padding);

    label {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      font-weight: 500;
      cursor: pointer;
    }
  }
`;

type UsersImportModalProps = {
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  isUploading: boolean;
  fileId: string | null;
  refetch: () => void;
  branchId?: string;
  groupId?: string;
} & DialogProps;

export const UsersImportModal: React.FC<UsersImportModalProps> = ({
  onHide,
  visible,
  setIsUploading,
  isUploading,
  fileId,
  refetch,
  branchId,
  groupId,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { canUseFeature } = useFeatureFlag();
  const { startTracking, waitForConnection } = useUsersImportMessage();

  const [stats, setStats] = useState<{
    usersAdded: number | undefined;
    usersUpdated: number | undefined;
    usersRemoved: number | undefined;
    usersWithUnverifiedDomain: number | undefined;
    usersSoftDeleted: number | undefined;
  }>({
    usersAdded: undefined,
    usersUpdated: undefined,
    usersRemoved: undefined,
    usersWithUnverifiedDomain: undefined,
    usersSoftDeleted: undefined,
  });
  const [skipNew, setSkipNew] = useState(true);
  const [skipExisting, setSkipExisting] = useState(true);
  const [deleteNotInFile, setDeleteNotInFile] = useState(false);
  const [restoreSoftDeleted, setRestoreSoftDeleted] = useState(false);

  const remainingSlots = currentAccount
    ? Math.max(0, FREE_TRIAL_USER_LIMIT - (currentAccount.totalUsersCount ?? 0))
    : Infinity;
  const isTrialImportCapped =
    !!currentAccount &&
    isTrialRestricted(currentAccount) &&
    canUseFeature(PostHogFeatureFlags.FREE_TRIAL_PLATFORM_RESTRICTIONS) &&
    stats.usersAdded !== undefined &&
    stats.usersAdded > remainingSlots;

  const hideModal = () => {
    setSkipNew(true);
    setSkipExisting(true);
    setDeleteNotInFile(false);
    setRestoreSoftDeleted(false);
    setStats({
      usersAdded: undefined,
      usersUpdated: undefined,
      usersRemoved: undefined,
      usersWithUnverifiedDomain: undefined,
      usersSoftDeleted: undefined,
    });
    onHide();
  };

  const { upload: uploadUsersAccountLevel } = useAccountUsersImport();
  const { upload: uploadUsersBranchLevel } = useBranchUsersImport();
  const { upload: uploadUsersGroupLevel } = useGroupUsersImport();

  const onUpload = async ({
    dryRun,
    skipNew = true,
    skipExisting = true,
    deleteNotInFile = false,
    restoreSoftDeleted = false,
  }: {
    dryRun: boolean;
    skipNew?: boolean;
    skipExisting?: boolean;
    deleteNotInFile?: boolean;
    restoreSoftDeleted?: boolean;
  }) => {
    if (!fileId) {
      return;
    }
    setIsUploading(true);

    const entity = branchId
      ? EntityEnum.BRANCH
      : groupId
      ? EntityEnum.GROUP
      : EntityEnum.ACCOUNT;
    const notificationId =
      entity === EntityEnum.BRANCH
        ? branchId
        : entity === EntityEnum.GROUP
        ? groupId
        : currentAccount?.id;

    if (!notificationId) {
      setIsUploading(false);
      return;
    }

    startTracking({
      entity,
      notificationId,
      isDryRun: dryRun,
      skipNew,
      skipExisting,
      deleteNotInFile,
      restoreSoftDeleted,
      onDryRunStats: ({
        usersAdded,
        usersUpdated,
        usersRemoved,
        usersWithUnverifiedDomain,
        usersSoftDeleted,
      }) => {
        setStats({
          usersAdded,
          usersUpdated,
          usersRemoved,
          usersWithUnverifiedDomain,
          usersSoftDeleted,
        });
      },
      onSettled: (settledDryRun) => {
        setIsUploading(false);
        refetch();

        if (!settledDryRun) {
          hideModal();
        }
      },
    });

    const isConnected = await waitForConnection(entity);

    if (!isConnected) {
      setIsUploading(false);
      toast?.error(t('toast.error'), t('users.import.fail'));
      return;
    }

    try {
      if (entity === EntityEnum.ACCOUNT) {
        await uploadUsersAccountLevel({
          accountId: currentAccount?.id as string,
          fileId,
          importKey: randomString(10),
          dryRun,
          skipNew,
          skipExisting,
          deleteNotInFile,
          restoreSoftDeleted,
        });
      }
      if (entity === EntityEnum.BRANCH && branchId) {
        await uploadUsersBranchLevel({
          accountId: (currentAccount as Account)?.id,
          branchId,
          fileId,
          importKey: randomString(10),
          dryRun,
          skipNew,
          skipExisting,
          deleteNotInFile,
          restoreSoftDeleted,
        });
      }
      if (entity === EntityEnum.GROUP && groupId) {
        await uploadUsersGroupLevel({
          accountId: (currentAccount as Account)?.id,
          groupId,
          fileId,
          importKey: randomString(10),
          dryRun,
          skipNew,
          skipExisting,
          deleteNotInFile,
          restoreSoftDeleted,
        });
      }
    } catch (e) {
      setIsUploading(false);
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  return (
    <StyledDialog
      id="users-import-modal"
      blockScroll
      visible={visible}
      header={<h1>{t('user.upload.csv.confirm')}</h1>}
      onHide={hideModal}
      draggable={false}
      data-testid="users-import-modal"
      closable={!isUploading}
      className="v2"
    >
      <FlexContainer direction="column" align="flex-start" justify="flex-start">
        <FlexContainer
          direction="column"
          align="flex-start"
          justify="flex-start"
        >
          <StyledDescription>
            {t('user.upload.csv.confirm.description')}
          </StyledDescription>
          <AppCheckbox
            id="add-new"
            key="add-new"
            label={t('user.upload.csv.confirm.addNew')}
            name="addNew"
            checked={!skipNew}
            onChange={(e: CheckboxChangeEvent) => {
              setSkipNew(!e.checked);
              onUpload({
                dryRun: true,
                skipNew: !e.checked,
                skipExisting,
                deleteNotInFile,
                restoreSoftDeleted,
              });
            }}
            disabled={isUploading}
          />
          <AppCheckbox
            id="update-existing"
            key="update-existing"
            label={t('user.upload.csv.confirm.update')}
            name="updateExisting"
            checked={!skipExisting}
            onChange={(e: CheckboxChangeEvent) => {
              setSkipExisting(!e.checked);
              onUpload({
                dryRun: true,
                skipExisting: !e.checked,
                skipNew,
                deleteNotInFile,
                restoreSoftDeleted,
              });
            }}
            disabled={isUploading}
          />
          <AppCheckbox
            id="remove-not-in-file"
            key="remove-not-in-file"
            label={t('user.upload.csv.confirm.deleteNotInFile')}
            name="deleteNotInFile"
            checked={deleteNotInFile}
            onChange={(e: CheckboxChangeEvent) => {
              setDeleteNotInFile(!!e.checked);
              onUpload({
                dryRun: true,
                deleteNotInFile: !!e.checked,
                skipNew,
                skipExisting,
                restoreSoftDeleted,
              });
            }}
            disabled={isUploading}
          />
          <StyledImpactMessage>
            <p>{t('user.upload.csv.confirm.warning')}</p>
            {(stats.usersAdded !== undefined ||
              stats.usersUpdated !== undefined ||
              stats.usersRemoved !== undefined) &&
            !isUploading ? (
              <FlexContainer align="center" justify="space-around">
                {stats.usersAdded !== undefined && (
                  <FlexContainer
                    flex="1"
                    align="center"
                    justify="center"
                    direction="column"
                  >
                    <div id="users-added-count" className="number">
                      {stats.usersAdded}
                    </div>
                    <p className="text">
                      {t('user.upload.csv.confirm.usersAdded')}
                    </p>
                  </FlexContainer>
                )}
                {stats.usersAdded !== undefined &&
                  (stats.usersUpdated !== undefined ||
                    stats.usersRemoved !== undefined) && (
                    <Divider layout="vertical" />
                  )}
                {stats.usersUpdated !== undefined && (
                  <FlexContainer
                    flex="1"
                    align="center"
                    justify="center"
                    direction="column"
                  >
                    <div id="users-updated-count" className="number">
                      {stats.usersUpdated}
                    </div>
                    <p className="text">
                      {t('user.upload.csv.confirm.usersUpdated')}
                    </p>
                  </FlexContainer>
                )}
                {stats.usersUpdated !== undefined &&
                  stats.usersRemoved !== undefined && (
                    <Divider layout="vertical" />
                  )}
                {stats.usersRemoved !== undefined && (
                  <FlexContainer
                    flex="1"
                    align="center"
                    justify="center"
                    direction="column"
                  >
                    <div id="users-removed-count" className="number">
                      {stats.usersRemoved}
                    </div>
                    <p className="text">
                      {t('user.upload.csv.confirm.usersRemoved')}
                    </p>
                  </FlexContainer>
                )}
              </FlexContainer>
            ) : isUploading ? (
              <AppSpinner width={40} height={40} strokeWidth={4} duration={1} />
            ) : stats.usersAdded === undefined &&
              stats.usersUpdated === undefined &&
              stats.usersRemoved === undefined &&
              !isUploading ? (
              <p id="no-changes" className="no-changes">
                {t('user.upload.csv.confirm.noChanges')}
              </p>
            ) : null}
          </StyledImpactMessage>
          {stats.usersSoftDeleted !== undefined &&
            stats.usersSoftDeleted > 0 && (
              <StyledSoftDeletedWarning id="soft-deleted-warning">
                <InfoCircleIcon />
                <div className="warning-text">
                  <p>
                    {t('user.upload.csv.confirm.softDeleted.warning', {
                      count: stats.usersSoftDeleted,
                    })}
                  </p>
                  <div className="restore-toggle">
                    <label htmlFor="restore-soft-deleted">
                      {t('user.upload.csv.confirm.softDeleted.toggle')}
                    </label>
                    <InputSwitch
                      id="restore-soft-deleted"
                      checked={restoreSoftDeleted}
                      className="v2 small"
                      onChange={(e) => {
                        const next = !!e.value;
                        setRestoreSoftDeleted(next);
                        onUpload({
                          dryRun: true,
                          skipNew,
                          skipExisting,
                          deleteNotInFile,
                          restoreSoftDeleted: next,
                        });
                      }}
                      disabled={isUploading}
                    />
                  </div>
                </div>
              </StyledSoftDeletedWarning>
            )}
          {getIsActivePhishingStatus(currentAccount) &&
            stats.usersWithUnverifiedDomain !== undefined &&
            stats.usersWithUnverifiedDomain > 0 && (
              <StyledUnverifiedDomainWarning id="unverified-domain-warning">
                <WarningTriangleIcon />
                <div className="warning-text">
                  <p>
                    {t('user.upload.csv.confirm.unverifiedDomain.message.1', {
                      count: stats.usersWithUnverifiedDomain,
                    })}
                    <br />
                    {t('user.upload.csv.confirm.unverifiedDomain.message.2')}
                  </p>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: t(
                        'user.upload.csv.confirm.unverifiedDomain.action',
                      ),
                    }}
                  />
                </div>
              </StyledUnverifiedDomainWarning>
            )}
          {isTrialImportCapped && (
            <StyledUnverifiedDomainWarning>
              <WarningTriangleIcon />
              <div className="warning-text">
                <p>
                  <strong>{t('user.freeTrial.limitReached.title')}</strong>
                </p>
                <p>
                  {t('user.freeTrial.import.warning.description', {
                    count: Math.max(0, remainingSlots),
                  })}
                </p>
                <Link
                  to={AppRoutesPaths.HUBSPOT_BOOKING_ROUTE}
                  style={{
                    fontSize: 'var(--small-font-size)',
                    fontWeight: 500,
                    color: 'var(--purple-dark)',
                    textDecoration: 'none',
                    borderBottom: '2px solid var(--gray-border-lighter)',
                    paddingBottom: '6px',
                    alignSelf: 'self-start',
                  }}
                >
                  {t('user.freeTrial.limitReached.bookMeeting')}
                </Link>
              </div>
            </StyledUnverifiedDomainWarning>
          )}
        </FlexContainer>
        <FlexContainer justify="flex-start" className="mt-5">
          <AppButton
            id="confirm-upload"
            state={isUploading ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
            label={t('user.upload.csv.confirm')}
            onClick={() => {
              onUpload({
                dryRun: false,
                skipNew,
                skipExisting,
                deleteNotInFile,
                restoreSoftDeleted,
              });
            }}
            isDisabled={
              isUploading ||
              (stats.usersAdded === undefined &&
                stats.usersUpdated === undefined &&
                stats.usersRemoved === undefined)
            }
          />
          <AppButton
            id="cancel-upload"
            label={t('button.cancel')}
            severity="secondary"
            type="outlined"
            onClick={() => {
              hideModal();
            }}
            className="ml-2"
            isDisabled={isUploading}
          />
        </FlexContainer>
      </FlexContainer>
    </StyledDialog>
  );
};
